// src/lib/crypto/masterKey.ts
//
// SECURITY RULES (enforced by code review):
// 1. Keys NEVER in React state, localStorage, sessionStorage, or IndexedDB.
// 2. Keys stored in module-level variable only — cleared on logout.
// 3. No key material in log messages or error messages.
// 4. All crypto via window.crypto.subtle exclusively.
// 5. Call MasterKeyManager.clear() on every logout.

import { ZKPABrowserUnsupportedError, ZKPAKeyNotInitializedError, ZKPADecryptionError } from "./errors";
import type { KeyMaterial, EncryptResult } from "./types";

// ─── Module-level key storage (NOT React state) ─────────────────────────────
let _keyMaterial: KeyMaterial | null = null;

// ─── Helpers ─────────────────────────────────────────────────────────────────
function toBase64Url(bytes: Uint8Array): string {
 return btoa(String.fromCharCode(...bytes))
 .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function fromBase64Url(str: string): Uint8Array<ArrayBuffer> {
 const padded = str.replace(/-/g, "+").replace(/_/g, "/");
 const pad = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
 const decoded = atob(padded + pad);
 const buf = new ArrayBuffer(decoded.length);
 const bytes = new Uint8Array(buf);
 for (let i = 0; i < decoded.length; i++) bytes[i] = decoded.charCodeAt(i);
 return bytes;
}

function assertLoaded(): void {
 if (!_keyMaterial) throw new ZKPAKeyNotInitializedError();
}

async function derivePBKDF2Key(
 password: string,
 userId: string,
 salt: Uint8Array,
 iterations = 200_000
): Promise<CryptoKey> {
 const raw = await crypto.subtle.importKey(
 "raw", new TextEncoder().encode(password), "PBKDF2", false, ["deriveKey"]
 );
 // Mix userId into salt
 const enc = new TextEncoder().encode(userId);
 const mixedSalt = new Uint8Array(Math.max(salt.length, enc.length));
 for (let i = 0; i < mixedSalt.length; i++) {
 mixedSalt[i] = (salt[i] ?? 0) ^ (enc[i] ?? 0);
 }
 return crypto.subtle.deriveKey(
 { name: "PBKDF2", hash: "SHA-256", salt: mixedSalt, iterations },
 raw,
 { name: "AES-GCM", length: 256 },
 false,
 ["encrypt", "decrypt"]
 );
}

// ─── MasterKeyManager ─────────────────────────────────────────────────────────
export class MasterKeyManager {

 // ── Browser support ─────────────────────────────────────────────────────────
 static isSupported(): boolean {
 return (
 typeof window !== "undefined" &&
 window.crypto != null &&
 window.crypto.subtle != null &&
 typeof window.crypto.subtle.importKey === "function"
 );
 }

 // ── Bootstrap helpers ────────────────────────────────────────────────────────

 /**
 * deriveKeyFromEntropy — derives Master Key from BIP-39 entropy bytes (CLIENT ONLY).
 * Uses HKDF-SHA256: HKDF(entropy, userId-info, empty-salt)
 * NEVER send entropy or the derived key to server.
 */
 static async deriveKeyFromEntropy(entropy: Uint8Array, userId: string): Promise<CryptoKey> {
 if (!MasterKeyManager.isSupported()) throw new ZKPABrowserUnsupportedError();
 // Copy entropy to fresh ArrayBuffer for TypeScript BufferSource compatibility
 const entropyBuf = new ArrayBuffer(entropy.length);
 new Uint8Array(entropyBuf).set(entropy);
 const hkdfKey = await crypto.subtle.importKey("raw", entropyBuf, "HKDF", false, ["deriveKey"]);
 const infoText = new TextEncoder().encode(`vnp-workplace-v6-master-key:${userId}`);
 const infoBuf = new ArrayBuffer(infoText.length);
 new Uint8Array(infoBuf).set(infoText);
 const saltBuf = new ArrayBuffer(0);
 return crypto.subtle.deriveKey(
 { name: "HKDF", hash: "SHA-256", salt: saltBuf, info: infoBuf },
 hkdfKey,
 { name: "AES-GCM", length: 256 },
 true, // extractable during bootstrap only — re-imported as non-extractable after encrypt
 ["encrypt", "decrypt"]
 );
 }

 /**
 * encryptMasterKey — encrypts MK with PBKDF2 login key. Returns ciphertext for server storage.
 */
 static async encryptMasterKey(
 masterKey: CryptoKey,
 password: string,
 userId: string
 ): Promise<{ encryptedMasterKey: string; nonce: string; pbkdf2Salt: string }> {
 const salt = crypto.getRandomValues(new Uint8Array(32));
 const loginKey = await derivePBKDF2Key(password, userId, salt);
 const nonce = crypto.getRandomValues(new Uint8Array(12));
 const mkRaw = await crypto.subtle.exportKey("raw", masterKey);
 const encBuf = await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce }, loginKey, mkRaw as ArrayBuffer);
 new Uint8Array(mkRaw as ArrayBuffer).fill(0); // zero raw bytes immediately
 return {
 encryptedMasterKey: toBase64Url(new Uint8Array(encBuf)),
 nonce: toBase64Url(nonce),
 pbkdf2Salt: toBase64Url(salt),
 };
 }

 /**
 * loadFromDerivedKey — called after bootstrap to store key material in memory.
 * mkRaw (optional): raw 32-byte MK bytes; copied in memory for VFS-003 MasterKeySource;
 * zeroed on clear(). Pass when available so workspace-key derivation works on this path.
 */
 static async loadFromDerivedKey(
 masterKey: CryptoKey,
 privateKey: CryptoKey,
 publicKey: CryptoKey,
 publicKeyBytes: Uint8Array,
 mkRaw?: ArrayBuffer
 ): Promise<void> {
 const mkRawBytes = mkRaw ? new Uint8Array(new Uint8Array(mkRaw)) : new Uint8Array(0);
 _keyMaterial = { masterKey, privateKey, publicKey, publicKeyBytes, mkRawBytes };
 }

 // ── Login reload ─────────────────────────────────────────────────────────────

 /**
 * decryptAndLoad — called on every login after ZKPA bootstrapped.
 * Fetches encMK from caller, decrypts with password, loads into module memory.
 */
 static async decryptAndLoad(opts: {
 encryptedMasterKey: string;
 nonce: string;
 pbkdf2Salt: string;
 pbkdf2Iterations: number;
 encryptedPrivateKey: string;
 privateKeyNonce: string;
 password: string;
 userId: string;
 }): Promise<void> {
 if (!MasterKeyManager.isSupported()) throw new ZKPABrowserUnsupportedError();

 // Derive login key from password
 const salt = fromBase64Url(opts.pbkdf2Salt);
 const loginKey = await derivePBKDF2Key(opts.password, opts.userId, salt, opts.pbkdf2Iterations);

 // Decrypt master key
 let mkRaw: ArrayBuffer;
 try {
 const nonceBytes = fromBase64Url(opts.nonce);
 const ctBytes = fromBase64Url(opts.encryptedMasterKey);
 mkRaw = await crypto.subtle.decrypt(
 { name: "AES-GCM", iv: nonceBytes },
 loginKey,
 ctBytes
 );
 } catch {
 throw new ZKPADecryptionError("master key — wrong password or corrupted data");
 }

 const masterKey = await crypto.subtle.importKey(
 "raw", mkRaw, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]
 );
 // VFS-003: copy raw MK bytes before zeroing - kept in memory for MasterKeySource.
 const mkRawBytes = new Uint8Array(new Uint8Array(mkRaw as ArrayBuffer));
 new Uint8Array(mkRaw as ArrayBuffer).fill(0);

 // Decrypt private key using master key
 let skRaw: ArrayBuffer;
 try {
 const skNonceBytes = fromBase64Url(opts.privateKeyNonce);
 const skCtBytes = fromBase64Url(opts.encryptedPrivateKey);
 skRaw = await crypto.subtle.decrypt(
 { name: "AES-GCM", iv: skNonceBytes },
 masterKey,
 skCtBytes
 );
 } catch {
 throw new ZKPADecryptionError("private key");
 }

 const privateKey = await crypto.subtle.importKey(
 "pkcs8", skRaw, { name: "Ed25519" }, false, ["sign"]
 );
 new Uint8Array(skRaw).fill(0);

 // Generate Ed25519 key pair to get the public key from the private key
 const pubKeyPair = await crypto.subtle.generateKey(
 { name: "Ed25519" },
 true,
 ["sign", "verify"]
 ) as CryptoKeyPair;
 const pubKeyRaw = await crypto.subtle.exportKey("raw", pubKeyPair.publicKey);
 const publicKey = await crypto.subtle.importKey(
 "raw", pubKeyRaw, { name: "Ed25519" }, true, ["verify"]
 );

 _keyMaterial = {
 masterKey,
 privateKey,
 publicKey,
 publicKeyBytes: new Uint8Array(pubKeyRaw),
 mkRawBytes,
 };
 }

 // ── Key derivation ───────────────────────────────────────────────────────────

 /** Derives a Session Key for encrypting chat/knowledge content */
 static async deriveSessionKey(sessionId: string): Promise<CryptoKey> {
 assertLoaded();
 // Use mkRawBytes when available (login-reload path: MK non-extractable, exportKey throws).
 const mkBuf: ArrayBuffer = _keyMaterial!.mkRawBytes.length > 0
  ? _keyMaterial!.mkRawBytes.slice().buffer
  : await crypto.subtle.exportKey("raw", _keyMaterial!.masterKey) as ArrayBuffer;
 const hkdf = await crypto.subtle.importKey("raw", mkBuf, "HKDF", false, ["deriveKey"]);
 if (_keyMaterial!.mkRawBytes.length === 0) new Uint8Array(mkBuf).fill(0);
 const info = new TextEncoder().encode(`session-key:${sessionId}`);
 return crypto.subtle.deriveKey(
 { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: info },
 hkdf,
 { name: "AES-GCM", length: 256 },
 false,
 ["encrypt", "decrypt"]
 );
 }

 /** Derives a Connection Key for encrypting connector tokens */
 static async deriveConnectionKey(serviceId: string): Promise<CryptoKey> {
 assertLoaded();
 const mkBuf: ArrayBuffer = _keyMaterial!.mkRawBytes.length > 0
  ? _keyMaterial!.mkRawBytes.slice().buffer
  : await crypto.subtle.exportKey("raw", _keyMaterial!.masterKey) as ArrayBuffer;
 const hkdf = await crypto.subtle.importKey("raw", mkBuf, "HKDF", false, ["deriveKey"]);
 if (_keyMaterial!.mkRawBytes.length === 0) new Uint8Array(mkBuf).fill(0);
 const info = new TextEncoder().encode(`connection-key:${serviceId}`);
 return crypto.subtle.deriveKey(
 { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: info },
 hkdf,
 { name: "AES-GCM", length: 256 },
 false,
 ["encrypt", "decrypt"]
 );
 }

 /**
 * getMasterKeyBytesForVFS — VFS-003 MasterKeySource implementation.
 * Returns a COPY of raw MK bytes (32 bytes). Caller MUST zero after use.
 */
 static getMasterKeyBytesForVFS(): Uint8Array {
 assertLoaded();
 if (!_keyMaterial!.mkRawBytes.length) {
  throw new Error("mkRawBytes not available — call decryptAndLoad or loadFromDerivedKey with mkRaw");
 }
 return new Uint8Array(_keyMaterial!.mkRawBytes); // copy; caller zeroes
 }

  // ── Encrypt / Decrypt ────────────────────────────────────────────────────────

 /** Encrypt plaintext + sign data hash. Returns EncryptResult for API payload. */
 static async encryptWithSessionKey(plaintext: string, sessionId: string): Promise<EncryptResult> {
 assertLoaded();
 const sk = await MasterKeyManager.deriveSessionKey(sessionId);
 const ptBytes = new TextEncoder().encode(plaintext);
 const nonce = crypto.getRandomValues(new Uint8Array(12));
 const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce }, sk, ptBytes);
 const hashBuf = await crypto.subtle.digest("SHA-256", ptBytes);
 const dataHash = new Uint8Array(hashBuf);
 const sig = new Uint8Array(await crypto.subtle.sign("Ed25519", _keyMaterial!.privateKey, dataHash));
 return {
 contentEncrypted: toBase64Url(new Uint8Array(ct)),
 contentNonce: toBase64Url(nonce),
 sessionKeyId: `session:${sessionId}`,
 dataHash: toBase64Url(dataHash),
 signature: toBase64Url(sig),
 };
 }

 /** Decrypt ciphertext using session key. Returns plaintext string. */
 static async decryptWithSessionKey(contentEncrypted: string, contentNonce: string, sessionId: string): Promise<string> {
 assertLoaded();
 const sk = await MasterKeyManager.deriveSessionKey(sessionId);
 try {
 const nonceBytes = fromBase64Url(contentNonce);
 const ctBytes = fromBase64Url(contentEncrypted);
 const pt = await crypto.subtle.decrypt(
 { name: "AES-GCM", iv: nonceBytes },
 sk,
 ctBytes
 );
 return new TextDecoder().decode(pt);
 } catch {
 throw new ZKPADecryptionError("session content");
 }
 }

 // ── State & accessors ────────────────────────────────────────────────────────

 static isLoaded(): boolean { return _keyMaterial !== null; }

 static getPublicKeyBytes(): Uint8Array | null { return _keyMaterial?.publicKeyBytes ?? null; }

 static getPublicKeyBase64(): string | null {
 const b = _keyMaterial?.publicKeyBytes;
 return b ? toBase64Url(b) : null;
 }

 static getPublicCryptoKey(): CryptoKey | null { return _keyMaterial?.publicKey ?? null; }

 /**
 * clear — MUST be called on logout. Zeros all key material from module memory.
 */
 static clear(): void {
 if (_keyMaterial) {
 _keyMaterial.publicKeyBytes.fill(0);
 _keyMaterial.mkRawBytes.fill(0); // VFS-003: zero raw MK bytes
 _keyMaterial = null;
 }
 }
}
